"use client"

import { Form, Formik, useFormikContext } from "formik"
import Link from "next/link"
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Save,
  Truck,
} from "lucide-react"

import { FormInput, FormSelect } from "@/components/form-inputs"
import { CrmReadyToShipStageField } from "@/components/deliveries/crm-ready-to-ship-stage-field"
import { CrmStatusFallbackSettings } from "@/components/deliveries/crm-status-fallback-settings"
import { DeliveryToolsRecommendation } from "@/components/deliveries/delivery-tools-recommendation"
import {
  IntegrationRequiredPlaceholder,
  IntegrationSectionLoadingPlaceholder,
} from "@/components/agents/shared/integration-required-placeholder"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useSaveAgentDeliverySettingsMutation } from "@/hooks/mutations/delivery-bindings"
import { useAgentDeliverySettingsQuery } from "@/hooks/queries/delivery-bindings"
import { useDeliveryIntegrationsQuery } from "@/hooks/queries/delivery-integrations"
import { usePermissions } from "@/hooks/use-permissions"
import {
  EMAIL_AGENTS_PERMISSIONS,
  INTEGRATIONS_PERMISSIONS,
  MESSAGING_AGENTS_PERMISSIONS,
  VOICE_AGENTS_PERMISSIONS,
} from "@/lib/auth/permissions.constants"
import type { Permission } from "@/lib/auth/permissions.types"
import {
  DEFAULT_DELIVERY_TOOL_SETTINGS,
  type DeliveryScopeType,
  type DeliveryCrmFieldMapping,
  type DeliveryToolName,
  type DeliveryToolSettings,
  type MerchandisePaymentPolicy,
} from "@/lib/deliveries/config"
import type { AgentDeliverySettingsView } from "@/server-actions/delivery-bindings"
import { DELIVERY_TOOL_SELECTION_HINT, getDeliveryToolRequirement, updateDeliveryToolSettings } from "@/lib/deliveries/tool-selection"
import type { DeliveryIntegrationView } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"




const SCOPE_MANAGE_PERMISSION: Record<DeliveryScopeType, Permission> = {
  email_agent: EMAIL_AGENTS_PERMISSIONS.MANAGE,
  message_agent: MESSAGING_AGENTS_PERMISSIONS.MANAGE,
  voice_agent: VOICE_AGENTS_PERMISSIONS.MANAGE,
}

function getTOOL_OPTIONS(tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesAgentDeliverySettings">>): ReadonlyArray<{
  name: DeliveryToolName
  label: string
  description: string
}> {
  return [
  {
    name: "resolve_product_price",
    label: tUi("verifyProductPrices"),
    description: tUi("verifyPricesFromTheSavedPromptOrKnowledge"),
  },
  {
    name: "delivery_search_locations",
    label: tUi("searchDeliveryLocations"),
    description: tUi("findNovaPostBranchesLockersAndPickupPoints"),
  },
  {
    name: "delivery_quote_shipment",
    label: tUi("quoteDelivery"),
    description: tUi("calculateTheCarrierPriceAndPrepareACustomer"),
  },
  {
    name: "delivery_create_shipment",
    label: tUi("createWaybill"),
    description: tUi("createTheWaybillOnlyAfterTrustedCustomerConfirmation"),
  },
  {
    name: "delivery_track_shipment",
    label: tUi("trackDelivery"),
    description: tUi("readTheCurrentStatusOfAKnownTracking"),
  },
]
}

type AgentDeliveryFormValues = {
  deliveryEnabled: boolean
  routingSelection: "inherit" | string
  merchandisePaymentPolicy:
    | "inherit_tenant_default"
    | "prepayment_only"
    | "cash_on_delivery_only"
    | "customer_choice"
  deliveryPriceResponsibility: "business" | "customer"
  crmStageId: string
  crmTags: string
  crmShippingAddressFieldCode: string
  crmWriteOpportunityPrice: boolean
  crmPaymentMethodFieldCode: string
  crmPrepaymentValue: string
  crmCashOnDeliveryValue: string
  crmDeliveryMethodFieldCode: string
  crmDeliveryMethodValue: string
  crmTrackingNumberFieldCode: string
  crmCarrierFieldCode: string
  crmOrderReferenceFieldCode: string
  crmPaymentStateFieldCode: string
  crmMerchandiseTotalFieldCode: string
  crmDeliveryFeeFieldCode: string
  crmOrderItemsFieldCode: string
  tools: DeliveryToolSettings
}

export function AgentDeliverySettings({
  agentId,
  scopeType,
  compact = false,
}: {
  agentId: string
  scopeType: DeliveryScopeType
  compact?: boolean
}) {


  const tUi = useTranslations("Deliveries.DeliveriesAgentDeliverySettings")
  const TOOL_OPTIONS = getTOOL_OPTIONS(tUi)


  const { can } = usePermissions()
  const settingsQuery = useAgentDeliverySettingsQuery(scopeType, agentId)
  const integrationsQuery = useDeliveryIntegrationsQuery()
  const saveMutation = useSaveAgentDeliverySettingsMutation()
  const canView = can(INTEGRATIONS_PERMISSIONS.VIEW)
  const canManage = can(INTEGRATIONS_PERMISSIONS.MANAGE)
    && can(SCOPE_MANAGE_PERMISSION[scopeType])

  if (!canView) {
    return (
      <Alert>
        <AlertDescription>
          <span key={tUi("youDoNotHavePermissionToViewDelivery")}>{tUi("youDoNotHavePermissionToViewDelivery")}</span></AlertDescription>
      </Alert>
    )
  }

  if (settingsQuery.isLoading) {
    if (compact) {
      return (
        <IntegrationSectionLoadingPlaceholder
          icon={Truck}
          title={tUi("deliveries")}
          description={tUi("configureDeliveryRoutingAndToolsForThisAgent")}
        />
      )
    }
    return <div className="h-80 animate-pulse rounded-md bg-muted" />
  }

  if (settingsQuery.error || !settingsQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription><span key={tUi("deliverySettingsCouldNotBeLoaded")}>{tUi("deliverySettingsCouldNotBeLoaded")}</span></AlertDescription>
      </Alert>
    )
  }

  if (integrationsQuery.isLoading) {
    if (compact) {
      return (
        <IntegrationSectionLoadingPlaceholder
          icon={Truck}
          title={tUi("deliveries")}
          description={tUi("configureDeliveryRoutingAndToolsForThisAgent")}
        />
      )
    }
    return <div className="h-80 animate-pulse rounded-md bg-muted" />
  }

  if (integrationsQuery.error) {
    return (
      <Alert variant="destructive">
        <AlertDescription><span key={tUi("deliverySettingsCouldNotBeLoaded")}>{tUi("deliverySettingsCouldNotBeLoaded")}</span></AlertDescription>
      </Alert>
    )
  }

  const activeIntegrations = (integrationsQuery.data ?? []).filter(
    integration => integration.isActive
      && integration.verificationStatus === "succeeded",
  )
  if (activeIntegrations.length === 0) {
    return <DeliveryIntegrationRequired />
  }

  const settings = settingsQuery.data
  if (!settings.crmHandoffConfigured) {
    return <CrmConnectionRequired />
  }

  return (
    <Card className={compact ? "min-h-80" : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          <span><span key={tUi("deliveries")}>{tUi("deliveries")}</span></span>
        </CardTitle>
        <CardDescription>
          <span key={tUi("chooseDeliveryRoutingAndControlWhichDeliveryOperations")}>{tUi("chooseDeliveryRoutingAndControlWhichDeliveryOperations")}</span></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Formik<AgentDeliveryFormValues>
          enableReinitialize
          initialValues={createInitialValues(settings, activeIntegrations)}
          validate={values => validateAgentSettings(
            values,
            settings,
            activeIntegrations,
          )}
          onSubmit={(values, helpers) => {
            const routingMode = getRoutingMode(
              values.deliveryEnabled,
              values.routingSelection,
            )
            saveMutation.mutate({
              scopeType,
              scopeId: agentId,
              routingMode,
              integrationId: resolveIntegrationId(
                values.routingSelection,
                routingMode,
                settings,
                activeIntegrations,
              ),
              merchandisePaymentPolicy: routingMode === "inherit"
                ? "inherit_tenant_default"
                : values.merchandisePaymentPolicy,
              deliveryPriceResponsibility: values.deliveryPriceResponsibility,
              crmSignal: routingMode === "integration"
                ? normalizeCrmSignal(values.crmStageId, values.crmTags)
                : undefined,
              crmFieldMapping: routingMode === "integration"
                ? normalizeCrmFieldMapping(values)
                : undefined,
              tools: values.tools,
              setErrors: helpers.setErrors,
            }, {
              onSettled: () => helpers.setSubmitting(false),
            })
          }}
        >
          {({ isSubmitting, setFieldValue, values }) => {
            const routingMode = getRoutingMode(
              values.deliveryEnabled,
              values.routingSelection,
            )
            const selectedIntegration = getSelectedIntegration(
              values.routingSelection,
              settings,
              activeIntegrations,
            )
            const cashOnDeliveryAvailable = selectedIntegration
              ? supportsCashOnDelivery(selectedIntegration)
              : false

            return (
              <Form className="space-y-6">
                <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
                  <div className="space-y-1">
                    <Label htmlFor="agent-delivery-enabled" className="text-base font-medium">
                      <span key={tUi("enableDeliveriesForThisAgent")}>{tUi("enableDeliveriesForThisAgent")}</span></Label>
                    <p className="text-sm text-muted-foreground">
                      <span key={tUi("allowThisAgentToUseItsSelectedDelivery")}>{tUi("allowThisAgentToUseItsSelectedDelivery")}</span></p>
                  </div>
                  <Switch
                    id="agent-delivery-enabled"
                    checked={values.deliveryEnabled}
                    disabled={!canManage || activeIntegrations.length === 0}
                    onCheckedChange={checked => {
                      void setFieldValue("deliveryEnabled", checked)
                    }}
                  />
                </div>

                {values.deliveryEnabled ? (
                  <>
                    <SavedConfigurationStatus
                      settings={settings}
                      scopeType={scopeType}
                      routingMode={routingMode}
                      selectedIntegration={selectedIntegration}
                      merchandisePaymentPolicy={values.merchandisePaymentPolicy}
                      tools={values.tools}
                    />
                    <DeliveryToolsRecommendation />

                    <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-muted-foreground">
                        <span key={tUi("senderIdentityBranchesCredentialsAndProviderDefaultsAre")}>{tUi("senderIdentityBranchesCredentialsAndProviderDefaultsAre")}</span></p>
                      <Button type="button" variant="outline" size="sm" asChild>
                        <Link href="/?page=integrations&section=deliveries#delivery-integrations">
                          <span><span key={tUi("configureDeliveryIntegration")}>{tUi("configureDeliveryIntegration")}</span></span>
                          <ExternalLink aria-hidden="true" className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>

                    <FormSelect
                      name="routingSelection"
                      label={tUi("deliveryIntegration")}
                      disabled={!canManage}
                      options={getRoutingOptions(settings, activeIntegrations)}
                      description={tUi("useTheTenantDefaultOrChooseADifferent")}
                    />

                    {routingMode !== "inherit" ? (
                      <>
                        <FormSelect
                          name="merchandisePaymentPolicy"
                          label={tUi("customerMerchandisePaymentOptions")}
                          disabled={!canManage}
                          options={getPaymentPolicyOptions(cashOnDeliveryAvailable, tUi)}
                          description={paymentPolicyDescription(
                            values.merchandisePaymentPolicy,
                            cashOnDeliveryAvailable, tUi
                          )}
                        />
                        <FormSelect
                          name="deliveryPriceResponsibility"
                          label={tUi("whoPaysTheCarrierDeliveryPrice")}
                          disabled={!canManage}
                          options={[
                            { value: "business", label: tUi("businessMerchant") },
                            { value: "customer", label: tUi("customer") },
                          ]}
                        />
                        {routingMode === "integration" ? (
                          <>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <CrmReadyToShipStageField
                                disabled={!canManage}
                                scope="agent"
                              />
                              <FormInput
                                name="crmTags"
                                label={tUi("cRMReadyToShipTags")}
                                maxLength={500}
                                disabled={!canManage}
                                description={tUi("optionalCommaSeparatedTagsTheWorkerNoteCannot")}
                              />
                            </div>
                            <CrmFieldMappingFields disabled={!canManage} />
                          </>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {<span key={tUi("effectivePaymentPolicyValue0", { value0: String(formatPaymentPolicy(settings.effectiveMerchandisePaymentPolicy, tUi)) })}>{tUi("effectivePaymentPolicyValue0", { value0: String(formatPaymentPolicy(settings.effectiveMerchandisePaymentPolicy, tUi)) })}</span>}
                      </p>
                    )}

                    <div className="space-y-3">
                      <div>
                        <h3 className="text-sm font-medium"><span key={tUi("agentDeliveryTools")}>{tUi("agentDeliveryTools")}</span></h3>
                        <p className="text-xs text-muted-foreground">
                          {DELIVERY_TOOL_SELECTION_HINT}
                        </p>
                      </div>
                      {TOOL_OPTIONS.map(tool => (
                        <div key={tool.name} className="space-y-2">
                          <DeliveryToolToggle {...tool} disabled={!canManage} />
                          {tool.name === "delivery_create_shipment" ? (
                            <CrmStatusFallbackSettings agentId={agentId} scopeType={scopeType} disabled={!canManage} />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}

                {canManage ? (
                  <div className="flex justify-center">
                    <Button
                      type="submit"
                      className="h-auto min-h-10 max-w-full whitespace-normal"
                      disabled={saveMutation.isPending || isSubmitting}
                    >
                      {saveMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      <span><span key={tUi("saveDeliverySettings")}>{tUi("saveDeliverySettings")}</span></span>
                    </Button>
                  </div>
                ) : null}
              </Form>
            )
          }}
        </Formik>
      </CardContent>
    </Card>
  )
}

function CrmConnectionRequired() {
  const tUi = useTranslations("Deliveries.DeliveriesAgentDeliverySettings")

  return (
    <IntegrationRequiredPlaceholder
      icon={Truck}
      showIllustration={false}
      sectionIcon={Truck}
      sectionIconTestId="delivery-crm-required-icon"
      sectionTitle={tUi("deliveries")}
      sectionDescription={tUi("configureDeliveryRoutingAndToolsForThisAgent")}
      iconTestId="delivery-crm-required-icon"
      title={tUi("connectACRMFirst")}
      description={tUi("crmRequiredDescription")}
      supportingText={tUi("crmRequiredSupportingText")}
    />
  )
}

function DeliveryIntegrationRequired() {
  const tUi = useTranslations("Deliveries.DeliveriesAgentDeliverySettings")

  return (
    <IntegrationRequiredPlaceholder
      icon={Truck}
      showIllustration={false}
      sectionIcon={Truck}
      sectionIconTestId="delivery-integration-required-icon"
      sectionTitle={tUi("deliveries")}
      sectionDescription={tUi("configureDeliveryRoutingAndToolsForThisAgent")}
      iconTestId="delivery-integration-required-icon"
      title={tUi("connectADeliveryProviderFirst")}
      description={tUi("providerRequiredDescription")}
      supportingText={tUi("providerRequiredSupportingText")}
      action={(
        <Button asChild>
          <Link href="/?page=integrations&section=deliveries#delivery-integrations">
            <span><span key={tUi("connectDeliveryProvider")}>{tUi("connectDeliveryProvider")}</span></span>
            <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      )}
    />
  )
}

function SavedConfigurationStatus({
  settings,
  scopeType,
  routingMode,
  selectedIntegration,
  merchandisePaymentPolicy,
  tools,
}: {
  settings: AgentDeliverySettingsView
  scopeType: DeliveryScopeType
  routingMode: ReturnType<typeof getRoutingMode>
  selectedIntegration: DeliveryIntegrationView | undefined
  merchandisePaymentPolicy: AgentDeliveryFormValues["merchandisePaymentPolicy"]
  tools: DeliveryToolSettings
}) {

  const tUi = useTranslations("Deliveries.DeliveriesAgentDeliverySettings")

  const hasPendingIntegrationSelection = !settings.effectiveBinding
    && routingMode === "integration"
    && selectedIntegration !== undefined
  const blockedReasons = getBlockedReasons({
    settings,
    routingMode,
    selectedIntegration,
    merchandisePaymentPolicy,
    tools,
  })
  if (blockedReasons.length > 0) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-medium"><span key={tUi("deliveryCreationIsBlocked")}>{tUi("deliveryCreationIsBlocked")}</span></p>
          <ul className="mt-1 list-disc pl-5">
            {blockedReasons.map(reason => <li key={reason}>{reason}</li>)}
          </ul>
        </AlertDescription>
      </Alert>
    )
  }

  if (hasPendingIntegrationSelection) {
    return (
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription>
          <span>{<span key={tUi("saveDeliverySettingsToEnableValue0ForThis", { value0: String(selectedIntegration.name) })}>{tUi("saveDeliverySettingsToEnableValue0ForThis", { value0: String(selectedIntegration.name) })}</span>}</span>
        </AlertDescription>
      </Alert>
    )
  }

  if (scopeType === "voice_agent") {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <span key={tUi("pricingLocationSearchQuotationAndTrackingSynchronizeTo")}>{tUi("pricingLocationSearchQuotationAndTrackingSynchronizeTo")}</span></AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert>
      <CheckCircle2 className="h-4 w-4" />
      <AlertDescription>
        <span key={tUi("deliveryPrerequisitesAreConfiguredRuntimeChecksStillValidate")}>{tUi("deliveryPrerequisitesAreConfiguredRuntimeChecksStillValidate")}</span></AlertDescription>
    </Alert>
  )
}

function CrmFieldMappingFields({ disabled }: { disabled: boolean }) {
  const tUi = useTranslations("Deliveries.DeliveriesAgentDeliverySettings")

  const { values, setFieldValue } = useFormikContext<AgentDeliveryFormValues>()
  return (
    <details className="rounded-md border p-4">
      <summary className="cursor-pointer text-sm font-medium">
        <span key={tUi("cRMDeliveryFieldMapping")}>{tUi("cRMDeliveryFieldMapping")}</span></summary>
      <p className="mt-2 text-xs text-muted-foreground">
        <span key={tUi("theDeliveryAddressUsesTheCRMSNative")}>{tUi("theDeliveryAddressUsesTheCRMSNative")}</span></p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <FormInput
          name="crmShippingAddressFieldCode"
          label={tUi("deliveryAddressFieldCodeOverride")}
          maxLength={200}
          disabled={disabled}
        />
        <div className="flex items-center justify-between gap-4 rounded-md border p-3">
          <div>
            <Label htmlFor="crm-write-opportunity-price">
              <span key={tUi("writeMerchandiseTotalToCRMOrderValue")}>{tUi("writeMerchandiseTotalToCRMOrderValue")}</span></Label>
            <p className="text-xs text-muted-foreground">
              <span key={tUi("enableOnlyWhenTheCRMOrderUsesThe")}>{tUi("enableOnlyWhenTheCRMOrderUsesThe")}</span></p>
          </div>
          <Switch
            id="crm-write-opportunity-price"
            checked={values.crmWriteOpportunityPrice}
            disabled={disabled}
            onCheckedChange={checked => {
              void setFieldValue("crmWriteOpportunityPrice", checked)
            }}
          />
        </div>
        <FormInput
          name="crmPaymentMethodFieldCode"
          label={tUi("paymentMethodFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmPrepaymentValue"
          label={tUi("cRMPrepaymentOptionIDOrLabel")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmCashOnDeliveryValue"
          label={tUi("cRMPaymentOnDeliveryOptionIDOrLabel")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmDeliveryMethodFieldCode"
          label={tUi("deliveryMethodFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmDeliveryMethodValue"
          label={tUi("cRMNovaPostOptionIDOrLabel")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmTrackingNumberFieldCode"
          label={tUi("trackingNumberCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmCarrierFieldCode"
          label={tUi("carrierCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmOrderReferenceFieldCode"
          label={tUi("orderReferenceCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmPaymentStateFieldCode"
          label={tUi("paymentStateCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmMerchandiseTotalFieldCode"
          label={tUi("merchandiseTotalCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmDeliveryFeeFieldCode"
          label={tUi("deliveryFeeCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
        <FormInput
          name="crmOrderItemsFieldCode"
          label={tUi("orderItemsCustomFieldCode")}
          maxLength={200}
          disabled={disabled}
        />
      </div>
    </details>
  )
}

function DeliveryToolToggle({
  name,
  label,
  description,
  disabled,
}: {
  name: DeliveryToolName
  label: string
  description: string
  disabled: boolean
}) {
  const { values, setFieldValue } = useFormikContext<AgentDeliveryFormValues>()
  const id = `delivery-tool-${name}`
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={id}>{label}</Label>
          <Badge variant="outline">{getDeliveryToolRequirement(name)}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        className="shrink-0"
        checked={values.tools[name]}
        disabled={disabled}
        onCheckedChange={checked => {
          void setFieldValue("tools", updateDeliveryToolSettings(values.tools, name, checked))
        }}
      />
    </div>
  )
}

function createInitialValues(
  settings: AgentDeliverySettingsView,
  integrations: DeliveryIntegrationView[],
): AgentDeliveryFormValues {
  const explicit = settings.explicitBinding
  let routingSelection: AgentDeliveryFormValues["routingSelection"] = "inherit"
  if (
    explicit
    && integrations.some(integration => integration.id === explicit.integrationId)
  ) {
    routingSelection = explicit.integrationId
  } else if (!settings.tenantDefaultBinding && integrations[0]) {
    routingSelection = integrations[0].id
  }

  return {
    deliveryEnabled: settings.deliveryEnabled,
    routingSelection,
    merchandisePaymentPolicy:
      explicit?.merchandisePaymentPolicy ?? "inherit_tenant_default",
    deliveryPriceResponsibility:
      explicit?.deliveryPriceResponsibility
      ?? settings.tenantDefaultBinding?.deliveryPriceResponsibility
      ?? "business",
    crmStageId: explicit?.crmSignal?.stageId ?? "",
    crmTags: explicit?.crmSignal?.tags?.join(", ") ?? "",
    crmShippingAddressFieldCode:
      explicit?.crmFieldMapping?.shippingAddressFieldCode ?? "",
    crmWriteOpportunityPrice:
      explicit?.crmFieldMapping?.writeMerchandiseTotalToOpportunityPrice
      ?? false,
    crmPaymentMethodFieldCode:
      explicit?.crmFieldMapping?.paymentMethod?.fieldCode ?? "",
    crmPrepaymentValue:
      explicit?.crmFieldMapping?.paymentMethod?.prepaymentValue ?? "",
    crmCashOnDeliveryValue:
      explicit?.crmFieldMapping?.paymentMethod?.cashOnDeliveryValue ?? "",
    crmDeliveryMethodFieldCode:
      explicit?.crmFieldMapping?.deliveryMethod?.fieldCode ?? "",
    crmDeliveryMethodValue:
      explicit?.crmFieldMapping?.deliveryMethod?.value ?? "",
    crmTrackingNumberFieldCode:
      explicit?.crmFieldMapping?.customFields?.trackingNumber ?? "",
    crmCarrierFieldCode:
      explicit?.crmFieldMapping?.customFields?.carrier ?? "",
    crmOrderReferenceFieldCode:
      explicit?.crmFieldMapping?.customFields?.orderReference ?? "",
    crmPaymentStateFieldCode:
      explicit?.crmFieldMapping?.customFields?.paymentState ?? "",
    crmMerchandiseTotalFieldCode:
      explicit?.crmFieldMapping?.customFields?.merchandiseTotal ?? "",
    crmDeliveryFeeFieldCode:
      explicit?.crmFieldMapping?.customFields?.deliveryFee ?? "",
    crmOrderItemsFieldCode:
      explicit?.crmFieldMapping?.customFields?.orderItems ?? "",
    tools: settings.tools ?? { ...DEFAULT_DELIVERY_TOOL_SETTINGS },
  }
}

function validateAgentSettings(
  values: AgentDeliveryFormValues,
  settings: AgentDeliverySettingsView,
  integrations: DeliveryIntegrationView[],
): Partial<Record<keyof AgentDeliveryFormValues, string>> {
  const errors: Partial<Record<keyof AgentDeliveryFormValues, string>> = {}
  if (!values.deliveryEnabled) return errors
  if (
    getRoutingMode(values.deliveryEnabled, values.routingSelection) === "integration"
    && !integrations.some(integration => integration.id === values.routingSelection)
  ) {
    errors.routingSelection = "Select an active delivery integration."
  }
  if (
    getRoutingMode(values.deliveryEnabled, values.routingSelection) === "inherit"
    && !settings.tenantDefaultBinding
  ) {
    errors.routingSelection = "Select an active delivery integration for this agent."
  }
  const paymentMappingValues = [
    values.crmPaymentMethodFieldCode,
    values.crmPrepaymentValue,
    values.crmCashOnDeliveryValue,
  ].filter(value => value.trim().length > 0)
  if (paymentMappingValues.length > 0 && paymentMappingValues.length < 3) {
    errors.crmPaymentMethodFieldCode =
      "Payment field code and both provider values are required together."
  }
  const deliveryMappingValues = [
    values.crmDeliveryMethodFieldCode,
    values.crmDeliveryMethodValue,
  ].filter(value => value.trim().length > 0)
  if (deliveryMappingValues.length === 1) {
    errors.crmDeliveryMethodFieldCode =
      "Delivery field code and provider value are required together."
  }
  return errors
}

function getRoutingMode(
  deliveryEnabled: boolean,
  routingSelection: AgentDeliveryFormValues["routingSelection"],
): "inherit" | "disabled" | "integration" {
  if (!deliveryEnabled) return "disabled"
  switch (routingSelection) {
    case "inherit":
      return "inherit"
    default:
      return "integration"
  }
}

function resolveIntegrationId(
  routingSelection: AgentDeliveryFormValues["routingSelection"],
  routingMode: ReturnType<typeof getRoutingMode>,
  settings: AgentDeliverySettingsView,
  integrations: DeliveryIntegrationView[],
): string | undefined {
  switch (routingMode) {
    case "inherit":
      return undefined
    case "integration":
      return routingSelection
    case "disabled":
      return (routingSelection !== "inherit" ? routingSelection : undefined)
        ?? settings.tenantDefaultBinding?.integrationId
        ?? settings.explicitBinding?.integrationId
        ?? integrations[0]?.id
    default: {
      const exhaustiveMode: never = routingMode
      return exhaustiveMode
    }
  }
}

function getSelectedIntegration(
  routingSelection: AgentDeliveryFormValues["routingSelection"],
  settings: AgentDeliverySettingsView,
  integrations: DeliveryIntegrationView[],
): DeliveryIntegrationView | undefined {
  const integrationId = routingSelection === "inherit"
    ? settings.tenantDefaultBinding?.integrationId
    : routingSelection
  return integrations.find(integration => integration.id === integrationId)
}

function getRoutingOptions(
  settings: AgentDeliverySettingsView,
  integrations: DeliveryIntegrationView[],
) {
  const integrationOptions = integrations.map(integration => ({
    value: integration.id,
    label: integration.name,
  }))
  if (!settings.tenantDefaultBinding) return integrationOptions
  return [
    {
      value: "inherit",
      label: `Use tenant default (${settings.tenantDefaultBinding.integration.name})`,
    },
    ...integrationOptions,
  ]
}

function getPaymentPolicyOptions(cashOnDeliveryAvailable: boolean, tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesAgentDeliverySettings">>) {
  const options = [
    { value: "inherit_tenant_default", label: tUi("inheritTenantDefault") },
    {
      value: "customer_choice",
      label: cashOnDeliveryAvailable
        ? "Let the customer choose"
        : "Use available methods (prepayment only)",
    },
    { value: "prepayment_only", label: tUi("prepaymentOnly") },
  ] as Array<{
    value: AgentDeliveryFormValues["merchandisePaymentPolicy"]
    label: string
  }>
  if (cashOnDeliveryAvailable) {
    options.push({
      value: "cash_on_delivery_only",
      label: tUi("cashOnDeliveryOnly"),
    })
  }
  return options
}

function paymentPolicyDescription(
  policy: AgentDeliveryFormValues["merchandisePaymentPolicy"],
  cashOnDeliveryAvailable: boolean,tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesAgentDeliverySettings">>
): string {
  switch (policy) {
    case "inherit_tenant_default":
      return tUi("usesTheMatchingMessagingEmailOrVoiceTenant")
    case "prepayment_only":
      return tUi("theCustomerPaysTheMerchantBeforeDeliveryThe")
    case "cash_on_delivery_only":
      return tUi("novaPostCollectsTheMerchandisePaymentAndSettles")
    case "customer_choice":
      return cashOnDeliveryAvailable
        ? "The bot offers prepayment and Nova Post payment on delivery, asks the customer to choose, and records the selection in CRM."
        : "Nova Post payment on delivery is not enabled for this connected sender account, so the bot uses prepayment and must not ask the customer to choose."
    default: {
      const exhaustivePolicy: never = policy
      return exhaustivePolicy
    }
  }
}

function normalizeCrmSignal(
  stageIdInput: string,
  tagsInput: string,
): { stageId?: string; tags?: string[] } | undefined {
  const stageId = stageIdInput.trim() || undefined
  const tags = tagsInput.split(",")
    .map(tag => tag.trim())
    .filter(Boolean)
  if (!stageId && tags.length === 0) return undefined
  return { stageId, tags: tags.length > 0 ? tags : undefined }
}

function normalizeCrmFieldMapping(
  values: AgentDeliveryFormValues,
): DeliveryCrmFieldMapping | undefined {
  const paymentMethod = values.crmPaymentMethodFieldCode.trim()
    ? {
        fieldCode: values.crmPaymentMethodFieldCode.trim(),
        prepaymentValue: values.crmPrepaymentValue.trim(),
        cashOnDeliveryValue: values.crmCashOnDeliveryValue.trim(),
      }
    : undefined
  const deliveryMethod = values.crmDeliveryMethodFieldCode.trim()
    ? {
        fieldCode: values.crmDeliveryMethodFieldCode.trim(),
        value: values.crmDeliveryMethodValue.trim(),
      }
    : undefined
  const customFields = removeEmptyValues({
    trackingNumber: values.crmTrackingNumberFieldCode,
    carrier: values.crmCarrierFieldCode,
    orderReference: values.crmOrderReferenceFieldCode,
    paymentState: values.crmPaymentStateFieldCode,
    merchandiseTotal: values.crmMerchandiseTotalFieldCode,
    deliveryFee: values.crmDeliveryFeeFieldCode,
    orderItems: values.crmOrderItemsFieldCode,
  })
  const mapping: DeliveryCrmFieldMapping = {
    ...(values.crmShippingAddressFieldCode.trim()
      ? { shippingAddressFieldCode: values.crmShippingAddressFieldCode.trim() }
      : {}),
    ...(values.crmWriteOpportunityPrice
      ? { writeMerchandiseTotalToOpportunityPrice: true }
      : {}),
    ...(paymentMethod ? { paymentMethod } : {}),
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
  }
  return Object.keys(mapping).length > 0 ? mapping : undefined
}

function removeEmptyValues<T extends Record<string, string>>(
  values: T,
): Partial<T> {
  const result: Partial<T> = {}
  for (const [key, value] of Object.entries(values) as Array<
    [keyof T, string]
  >) {
    const normalized = value.trim()
    if (normalized) result[key] = normalized as T[keyof T]
  }
  return result
}

function getBlockedReasons({
  settings,
  routingMode,
  selectedIntegration,
  merchandisePaymentPolicy,
  tools,
}: {
  settings: AgentDeliverySettingsView
  routingMode: ReturnType<typeof getRoutingMode>
  selectedIntegration: DeliveryIntegrationView | undefined
  merchandisePaymentPolicy: AgentDeliveryFormValues["merchandisePaymentPolicy"]
  tools: DeliveryToolSettings
}): string[] {
  const reasons: string[] = []
  if (settings.agentToolsRolloutEnabled === false) {
    reasons.push(
      "Delivery agent tools are awaiting staging migration and live price-verification approval.",
    )
  }
  const effectiveIntegration = selectedIntegration
    ?? settings.effectiveBinding?.integration
  if (!effectiveIntegration) {
    reasons.push("No active delivery integration resolves for this agent.")
  }
  if (
    tools.delivery_create_shipment
    && !tools.delivery_quote_shipment
  ) {
    reasons.push("Waybill creation requires delivery quotation.")
  }
  if (
    getEffectivePaymentPolicy(routingMode, merchandisePaymentPolicy, settings)
      === "cash_on_delivery_only"
    && !effectiveIntegration?.supportsCashOnDelivery
  ) {
    reasons.push("Cash on delivery is not verified for the selected integration.")
  }
  return reasons
}

function getEffectivePaymentPolicy(
  routingMode: ReturnType<typeof getRoutingMode>,
  merchandisePaymentPolicy: AgentDeliveryFormValues["merchandisePaymentPolicy"],
  settings: AgentDeliverySettingsView,
): MerchandisePaymentPolicy {
  switch (routingMode) {
    case "integration":
      switch (merchandisePaymentPolicy) {
        case "inherit_tenant_default":
          return settings.tenantDefaultBinding
            ? settings.effectiveMerchandisePaymentPolicy
            : "customer_choice"
        case "prepayment_only":
        case "cash_on_delivery_only":
        case "customer_choice":
          return merchandisePaymentPolicy
        default: {
          const exhaustivePolicy: never = merchandisePaymentPolicy
          return exhaustivePolicy
        }
      }
    case "inherit":
    case "disabled":
      return settings.effectiveMerchandisePaymentPolicy
    default: {
      const exhaustiveMode: never = routingMode
      return exhaustiveMode
    }
  }
}

function supportsCashOnDelivery(
  integration: DeliveryIntegrationView,
): boolean {
  return integration.supportsCashOnDelivery
}

function formatPaymentPolicy(
  policy: AgentDeliverySettingsView["effectiveMerchandisePaymentPolicy"],tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesAgentDeliverySettings">>
): string {
  switch (policy) {
    case "prepayment_only":
      return tUi("prepaymentOnly")
    case "cash_on_delivery_only":
      return tUi("cashOnDeliveryOnly")
    case "customer_choice":
      return tUi("letTheCustomerChoose")
    default: {
      const exhaustivePolicy: never = policy
      return exhaustivePolicy
    }
  }
}
